'use client';

import { useTranslations } from 'next-intl';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { cn } from '@/app/utils';
import type { ClawSkillGroup, ClawSkillItem } from '../../../_hook/use-claw-skill-search';

interface SkillSuggestionListProps {
  items: ClawSkillItem[];
  command: (item: ClawSkillItem) => void;
  query?: string;
}

/**
 * `/` 唤起的技能选择器列表，键盘交互（上/下/回车）与 MentionList 对齐。
 * 列表按来源分组（已安装 / 平台），分组标题仅作分隔，不占可选索引。
 * 通过 ReactRenderer 挂载，onKeyDown 由 suggestion 插件透传。
 */
export const SkillSuggestionList = forwardRef<
  { onKeyDown: (event: { key: string }) => boolean },
  SkillSuggestionListProps
>(({ items, command, query = '' }, ref) => {
  const t = useTranslations('claw.chat.skill');
  const [selectedIndex, setSelectedIndex] = useState(0);
  // 每个可选项 button 的 DOM 引用，按 items 索引存；分组标题不入此表，保证滚动定位准确。
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const selectItem = (index: number) => {
    const item = items[index];
    if (item) command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ key }) => {
      if (key === 'ArrowUp') {
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
        return true;
      }
      if (key === 'ArrowDown') {
        setSelectedIndex((prev) => (prev + 1) % items.length);
        return true;
      }
      if (key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) return null;

  const groupTitle = (group: ClawSkillGroup) => (group === 'installed' ? t('installedGroup') : t('platformGroup'));

  const highlightMatch = (text: string) => {
    if (!query) return text;
    const startIndex = text.toLowerCase().indexOf(query.toLowerCase());
    if (startIndex === -1) return text;
    return (
      <>
        {text.slice(0, startIndex)}
        <span className="text-system-blue02">{text.slice(startIndex, startIndex + query.length)}</span>
        {text.slice(startIndex + query.length)}
      </>
    );
  };

  return (
    <div className="items-list max-h-96 w-[720px] max-w-[calc(100vw-32px)] overflow-y-auto rounded-12 bg-[#2C323D] p-2">
      {items.map((item, index) => {
        const showGroupTitle = index === 0 || items[index - 1].group !== item.group;
        return (
          <div key={item.id}>
            {showGroupTitle ? (
              <div className="px-2.5 pt-2 pb-1 text-system-white40 text-xs first:pt-0">{groupTitle(item.group)}</div>
            ) : null}
            <button
              type="button"
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-8 p-2.5 text-left',
                selectedIndex === index ? 'bg-[#3E4451]' : '',
              )}
              onClick={() => selectItem(index)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="flex-shrink-0 text-sm text-system-white40">/</span>
              <span className="max-w-[180px] shrink-0 truncate text-sm text-white">{highlightMatch(item.label)}</span>
              <span className="min-w-0 flex-1 truncate text-system-white40 text-xs">{item.description}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
});

SkillSuggestionList.displayName = 'SkillSuggestionList';
